import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Row,
  Col,
  Tag,
  Progress,
  Image,
  Button,
  Typography,
  Spin,
  Modal,
  Input,
  message,
  Space,
  Divider,
  List,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  getSubmissionDetail,
  reviewSubmission,
  requestResubmission,
  type KycSubmissionDetail,
  type KycStatus,
  type OnboardingChecklistDto,
} from '../../services/adminKycApiService';

const { Text, Title, Paragraph } = Typography;

const NO_IMAGE_FALLBACK =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='150'%3E%3Crect width='100%25' height='100%25' fill='%23f5f5f5'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='14' fill='%23aaa' text-anchor='middle' dy='.3em'%3ENo image%3C/text%3E%3C/svg%3E";

const STATUS_COLORS: Record<KycStatus, string> = {
  PENDING: 'gold',
  AI_REVIEWING: 'processing',
  MANUAL_REVIEW: 'orange',
  APPROVED: 'success',
  REJECTED: 'error',
  EXPIRED: 'default',
};

const DOC_LABELS: Record<string, string> = {
  SA_SMART_ID: 'Smart ID card',
  SA_GREEN_ID: 'Green ID book',
  PASSPORT: 'Passport',
};

const CHECKLIST_ITEMS: { label: string; field: keyof OnboardingChecklistDto }[] = [
  { label: 'KYC Approved', field: 'kycApproved' },
  { label: 'Profile Complete', field: 'profileComplete' },
  { label: 'Bank Verified', field: 'bankVerified' },
  { label: 'Trade Cert Uploaded', field: 'tradeCertUploaded' },
  { label: 'Background Check', field: 'backgroundCheckPassed' },
  { label: 'Terms Accepted', field: 'termsAccepted' },
];

export interface KycReviewDrawerProps {
  submissionId: string | null;
  open: boolean;
  onClose: () => void;
  onActionComplete: () => void;
}

/** Side drawer for reviewing a single KYC submission — approve, reject, or request resubmission. */
export function KycReviewDrawer({
  submissionId,
  open,
  onClose,
  onActionComplete,
}: KycReviewDrawerProps) {
  const [detail, setDetail] = useState<KycSubmissionDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectVisible, setRejectVisible] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [resubVisible, setResubVisible] = useState(false);
  const [resubReason, setResubReason] = useState('');

  useEffect(() => {
    if (open && submissionId) {
      setDetail(null);
      setLoading(true);
      getSubmissionDetail(submissionId)
        .then(setDetail)
        .catch(() => { void message.error('Failed to load submission details'); })
        .finally(() => setLoading(false));
    }
  }, [open, submissionId]);

  const handleApprove = () => {
    Modal.confirm({
      title: 'Approve this submission?',
      content: 'This will mark the provider as KYC-verified.',
      okText: 'Approve',
      okType: 'primary',
      onOk: async () => {
        setActionLoading(true);
        try {
          await reviewSubmission(submissionId!, 'APPROVED');
          void message.success('Submission approved successfully');
          onActionComplete();
        } catch {
          void message.error('Failed to approve submission');
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return;
    setActionLoading(true);
    try {
      await reviewSubmission(submissionId!, 'REJECTED', rejectReason.trim());
      void message.success('Submission rejected');
      setRejectVisible(false);
      setRejectReason('');
      onActionComplete();
    } catch {
      void message.error('Failed to reject submission');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResubmit = async () => {
    if (!resubReason.trim()) return;
    setActionLoading(true);
    try {
      await requestResubmission(submissionId!, resubReason.trim());
      void message.success('Resubmission requested');
      setResubVisible(false);
      setResubReason('');
      onActionComplete();
    } catch {
      void message.error('Failed to request resubmission');
    } finally {
      setActionLoading(false);
    }
  };

  const verdictColor = (v: string | null) => {
    if (!v) return 'default';
    const map: Record<string, string> = { PASS: 'success', FAIL: 'error', MANUAL_REVIEW: 'warning' };
    return map[v] ?? 'default';
  };

  const footer = (
    <Space>
      <Button
        type="primary"
        style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
        onClick={handleApprove}
        loading={actionLoading}
        disabled={!detail}
      >
        Approve
      </Button>
      <Button
        style={{ borderColor: '#faad14', color: '#faad14' }}
        onClick={() => setResubVisible(true)}
        disabled={!detail || actionLoading}
      >
        Request Resubmission
      </Button>
      <Button
        danger
        onClick={() => setRejectVisible(true)}
        disabled={!detail || actionLoading}
      >
        Reject
      </Button>
    </Space>
  );

  return (
    <>
      <Drawer
        title="KYC Submission Review"
        width={960}
        placement="right"
        open={open}
        onClose={onClose}
        footer={footer}
        destroyOnClose
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: 80 }}>
            <Spin size="large" />
          </div>
        ) : detail ? (
          <Row gutter={24}>
            {/* LEFT — Provider info */}
            <Col span={7}>
              <Title level={5} style={{ marginTop: 0 }}>Provider Info</Title>
              <Space direction="vertical" style={{ width: '100%' }} size={4}>
                <div>
                  <Text type="secondary">Provider ID</Text>
                  <br />
                  <Text code style={{ fontSize: 11 }}>{detail.providerId}</Text>
                </div>
                <div>
                  <Text type="secondary">Document Type</Text>
                  <br />
                  <Text strong>{DOC_LABELS[detail.documentType] ?? detail.documentType}</Text>
                </div>
                <div>
                  <Text type="secondary">Submitted</Text>
                  <br />
                  <Text>{dayjs(detail.submittedAt).format('DD/MM/YYYY HH:mm')}</Text>
                </div>
                {detail.reviewedAt && (
                  <div>
                    <Text type="secondary">Reviewed</Text>
                    <br />
                    <Text>{dayjs(detail.reviewedAt).format('DD/MM/YYYY HH:mm')}</Text>
                  </div>
                )}
                <div>
                  <Text type="secondary">Status</Text>
                  <br />
                  <Tag color={STATUS_COLORS[detail.status]}>{detail.status.replace(/_/g, ' ')}</Tag>
                </div>
                {detail.rejectionReason && (
                  <div>
                    <Text type="secondary">Rejection reason</Text>
                    <br />
                    <Text type="danger">{detail.rejectionReason}</Text>
                  </div>
                )}
              </Space>

              <Divider />
              <Title level={5}>Onboarding Checklist</Title>
              <List
                size="small"
                dataSource={CHECKLIST_ITEMS}
                renderItem={(item) => (
                  <List.Item style={{ padding: '4px 0', border: 'none' }}>
                    {detail.onboardingChecklist[item.field] ? (
                      <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                    ) : (
                      <CloseCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                    )}
                    <Text style={{ fontSize: 13 }}>{item.label}</Text>
                  </List.Item>
                )}
              />
            </Col>

            {/* CENTRE — Document images */}
            <Col span={9}>
              <Title level={5} style={{ marginTop: 0 }}>Documents</Title>
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <div>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                    Front of document
                  </Text>
                  <Image
                    src={detail.docFrontUrl ?? undefined}
                    fallback={NO_IMAGE_FALLBACK}
                    alt="Front of document"
                    style={{ width: '100%', borderRadius: 6, border: '1px solid #f0f0f0' }}
                  />
                </div>
                {detail.docBackUrl && (
                  <div>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                      Back of document
                    </Text>
                    <Image
                      src={detail.docBackUrl}
                      fallback={NO_IMAGE_FALLBACK}
                      alt="Back of document"
                      style={{ width: '100%', borderRadius: 6, border: '1px solid #f0f0f0' }}
                    />
                  </div>
                )}
                <div>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                    Selfie
                  </Text>
                  <Image
                    src={detail.selfieUrl ?? undefined}
                    fallback={NO_IMAGE_FALLBACK}
                    alt="Selfie"
                    style={{ width: 160, borderRadius: 6, border: '1px solid #f0f0f0' }}
                  />
                </div>
              </Space>
            </Col>

            {/* RIGHT — AI analysis */}
            <Col span={8}>
              <Title level={5} style={{ marginTop: 0 }}>AI Analysis</Title>
              <Space direction="vertical" style={{ width: '100%' }} size={12}>
                <div>
                  <Text type="secondary">Verdict</Text>
                  <br />
                  {detail.aiVerdict ? (
                    <Tag color={verdictColor(detail.aiVerdict)} style={{ marginTop: 4 }}>
                      {detail.aiVerdict}
                    </Tag>
                  ) : (
                    <Tag color="default">Pending</Tag>
                  )}
                </div>

                {detail.aiConfidence !== null && (
                  <div>
                    <Text type="secondary">Confidence score</Text>
                    <Progress
                      percent={detail.aiConfidence}
                      size="small"
                      strokeColor={
                        detail.aiConfidence >= 80
                          ? '#52c41a'
                          : detail.aiConfidence >= 50
                          ? '#faad14'
                          : '#ff4d4f'
                      }
                      style={{ marginTop: 4 }}
                    />
                  </div>
                )}

                {detail.aiSummary && (
                  <div>
                    <Text type="secondary">AI summary</Text>
                    <Paragraph style={{ marginTop: 4, fontSize: 13 }}>
                      {detail.aiSummary}
                    </Paragraph>
                  </div>
                )}

                {detail.aiFlags && detail.aiFlags.length > 0 && (
                  <div>
                    <Text type="secondary">Flags ({detail.aiFlags.length})</Text>
                    <List
                      size="small"
                      dataSource={detail.aiFlags}
                      renderItem={(flag) => (
                        <List.Item style={{ padding: '4px 0', border: 'none' }}>
                          <WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />
                          <Text style={{ fontSize: 13 }}>{flag}</Text>
                        </List.Item>
                      )}
                    />
                  </div>
                )}

                {(!detail.aiFlags || detail.aiFlags.length === 0) && !detail.aiSummary && !detail.aiVerdict && (
                  <div style={{ color: '#aaa', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <MinusCircleOutlined />
                    <Text type="secondary">AI analysis not yet available</Text>
                  </div>
                )}

                {detail.aiFlags && detail.aiFlags.length === 0 && detail.aiVerdict && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ExclamationCircleOutlined style={{ color: '#52c41a' }} />
                    <Text style={{ fontSize: 13 }}>No flags detected</Text>
                  </div>
                )}
              </Space>
            </Col>
          </Row>
        ) : (
          <div style={{ textAlign: 'center', padding: 80, color: '#aaa' }}>
            No submission selected
          </div>
        )}
      </Drawer>

      {/* Reject modal */}
      <Modal
        title="Reject Submission"
        open={rejectVisible}
        onCancel={() => { setRejectVisible(false); setRejectReason(''); }}
        onOk={handleReject}
        okText="Reject"
        okButtonProps={{ danger: true, loading: actionLoading, disabled: !rejectReason.trim() }}
        destroyOnClose
      >
        <Input.TextArea
          rows={4}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="Explain why this submission is being rejected..."
          maxLength={500}
          showCount
          style={{ marginTop: 8 }}
        />
      </Modal>

      {/* Request resubmission modal */}
      <Modal
        title="Request Resubmission"
        open={resubVisible}
        onCancel={() => { setResubVisible(false); setResubReason(''); }}
        onOk={handleResubmit}
        okText="Send Request"
        okButtonProps={{ loading: actionLoading, disabled: !resubReason.trim() }}
        destroyOnClose
      >
        <Input.TextArea
          rows={4}
          value={resubReason}
          onChange={(e) => setResubReason(e.target.value)}
          placeholder="Explain what the provider needs to fix or resubmit..."
          maxLength={500}
          showCount
          style={{ marginTop: 8 }}
        />
      </Modal>
    </>
  );
}

export default KycReviewDrawer;
